/**
 * Modal para mostrar detalles de usuario
 * Sistema Klever Octopus - Betel Natural
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../../components/UI/Modal';

// Mapas de visualización para los campos de vendedor (formato backend -> texto)
const PAYMENT_PERIOD_LABELS = {
  MONTHLY: 'Mensual',
  BIWEEKLY: 'Quincenal',
  WEEKLY: 'Semanal'
};

const PAYMENT_METHOD_LABELS = {
  BANK_TRANSFER: 'Depósito bancario',
  CASH: 'Efectivo',
  CHECK: 'Cheque',
  ZELLE: 'Zelle'
};

const UserDetailModal = ({ user, isOpen, onClose, onEdit = null }) => {
  if (!user) return null;

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Nombre: la API entrega `full_name`; respaldo a nombre/apellido o email
  const displayName =
    user.full_name ||
    `${user.nombre || ''} ${user.apellido || ''}`.trim() ||
    user.email ||
    'Usuario';

  // Rol: primero RBAC (assigned_roles), luego enum legacy (role)
  const userRole =
    user.assigned_roles && user.assigned_roles.length > 0
      ? user.assigned_roles[0]
      : null;
  const roleName =
    userRole?.display_name ||
    userRole?.name ||
    (typeof user.role === 'string' ? user.role : null) ||
    'Sin rol asignado';
  const roleColor = userRole?.color || '#6c757d';
  const roleDescription = userRole?.description;
  const roleLevel = userRole?.nivel_acceso || userRole?.level;

  const phone = user.phone || user.telefono;

  // Vendedor: campos planos del backend
  const isVendor =
    userRole?.name === 'vendedor' ||
    userRole?.name === 'sales' ||
    user.commission_percentage != null;

  const bankAccount = user.bank_account;

  const permissions = Array.isArray(user.permissions) ? user.permissions : [];

  const lastLogin = formatDate(user.last_login_at || user.last_login);

  return (
    <Modal
      title={`Detalles del Usuario - ${displayName}`}
      isOpen={isOpen}
      onClose={onClose}
      size="large"
    >
      <div className="user-detail-modal">
        <div className="detail-sections">
          {/* Información Personal */}
          <div className="detail-section">
            <h3 className="section-title">
              <i className="fas fa-user"></i>
              Información Personal
            </h3>

            <div className="detail-grid">
              <div className="detail-item">
                <span className="item-label">Nombre Completo</span>
                <span className="item-value">{displayName}</span>
              </div>

              <div className="detail-item">
                <span className="item-label">Email</span>
                <span className="item-value">{user.email}</span>
              </div>

              <div className="detail-item">
                <span className="item-label">Teléfono</span>
                <span className="item-value">{phone || 'No especificado'}</span>
              </div>

              <div className="detail-item">
                <span className="item-label">Estado</span>
                <span className={`item-value ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                  <i className={`fas fa-circle ${user.is_active ? 'text-success' : 'text-danger'}`}></i>
                  {user.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </div>

          {/* Rol y Permisos */}
          <div className="detail-section">
            <h3 className="section-title">
              <i className="fas fa-user-shield"></i>
              Rol y Permisos
            </h3>

            <div className="detail-grid">
              <div className="detail-item">
                <span className="item-label">Rol Asignado</span>
                <span className="item-value">
                  <span
                    className="role-badge"
                    style={{ backgroundColor: roleColor, color: 'white' }}
                  >
                    {roleName}
                  </span>
                </span>
              </div>

              <div className="detail-item">
                <span className="item-label">Nivel de Acceso</span>
                <span className="item-value">
                  {roleLevel ? (
                    <span className={`level-badge level-${roleLevel}`}>
                      Nivel {roleLevel}
                    </span>
                  ) : 'No definido'}
                </span>
              </div>
            </div>

            {roleDescription && (
              <div className="detail-item full-width">
                <span className="item-label">Descripción del Rol</span>
                <span className="item-value">{roleDescription}</span>
              </div>
            )}

            {/* Permisos adicionales */}
            {permissions.length > 0 && (
              <div className="detail-item full-width">
                <span className="item-label">Permisos</span>
                <div className="permissions-list">
                  {permissions.map((permission, index) => (
                    <span key={permission.id || permission.name || index} className="permission-tag">
                      {typeof permission === 'string'
                        ? permission
                        : permission.display_name || permission.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Información de Vendedor (si aplica) */}
          {isVendor && (
            <div className="detail-section">
              <h3 className="section-title">
                <i className="fas fa-percentage"></i>
                Información de Vendedor
              </h3>

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="item-label">Comisión</span>
                  <span className="item-value">{user.commission_percentage ?? 0}%</span>
                </div>

                <div className="detail-item">
                  <span className="item-label">Frecuencia de Pago</span>
                  <span className="item-value">
                    {PAYMENT_PERIOD_LABELS[user.payment_period] || 'No especificado'}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="item-label">Forma de Pago</span>
                  <span className="item-value">
                    {PAYMENT_METHOD_LABELS[user.payment_method] || 'No especificado'}
                  </span>
                </div>
              </div>

              <h4 className="subsection-title">Datos Bancarios</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="item-label">Banco</span>
                  <span className="item-value">{user.bank_name || 'No especificado'}</span>
                </div>

                <div className="detail-item">
                  <span className="item-label">Número de Cuenta</span>
                  <span className="item-value">
                    {bankAccount ? `****${String(bankAccount).slice(-4)}` : 'No especificado'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Información del Sistema */}
          <div className="detail-section">
            <h3 className="section-title">
              <i className="fas fa-info-circle"></i>
              Información del Sistema
            </h3>

            <div className="detail-grid">
              <div className="detail-item">
                <span className="item-label">ID de Usuario</span>
                <span className="item-value">#{user.id}</span>
              </div>

              <div className="detail-item">
                <span className="item-label">Fecha de Creación</span>
                <span className="item-value">{formatDate(user.created_at) || 'N/A'}</span>
              </div>

              <div className="detail-item">
                <span className="item-label">Última Actualización</span>
                <span className="item-value">{formatDate(user.updated_at) || 'N/A'}</span>
              </div>

              <div className="detail-item">
                <span className="item-label">Último Acceso</span>
                <span className="item-value">{lastLogin || 'Nunca'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Acciones */}
        {onEdit && (
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onEdit}
            >
              <i className="fas fa-edit"></i>
              Editar Usuario
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

UserDetailModal.propTypes = {
  user: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func
};

export default UserDetailModal;
